/**
 * widgets/NodePicker.tsx — výběr cílového nodu (mesh peer). Používá ho hlavička
 * záložky (default nod) i edit akce karty (override). Hodnota '' = lokál (null).
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { Select } from '../components/ui/Select';
import {
  mergeReachableNodeTargets,
  nodeDisplayLabel,
  resolveNodeDisplayLabel,
  type MeshPeer,
} from './node-targets';

export type { MeshPeer } from './node-targets';

/** Normalizuj odpověď mesh.peers_list (array i { nodes }) na seznam peerů. */
export function normalizeMeshPeers(raw: unknown): MeshPeer[] {
  if (Array.isArray(raw)) {
    return raw
      .map((p) => normalizeOnePeer(p))
      .filter((p): p is MeshPeer => !!p);
  }
  if (raw && typeof raw === 'object') {
    const nodes = (raw as { nodes?: unknown }).nodes;
    if (Array.isArray(nodes)) {
      return nodes
        .map((p) => normalizeOnePeer(p))
        .filter((p): p is MeshPeer => !!p);
    }
  }
  return [];
}

function normalizeOnePeer(p: unknown): MeshPeer | null {
  if (!p || typeof p !== 'object') return null;
  const o = p as Record<string, unknown>;
  // Trust Anchors historicky četlo hostname; peers_list vrací name.
  const name = String(o.name ?? o.hostname ?? o.node_id ?? '').trim();
  if (!name) return null;
  const online = o.online === true || o.status === 'online'
    ? true
    : o.online === false || o.status === 'offline'
    ? false
    : undefined;
  return {
    name,
    stable_id: typeof o.stable_id === 'string' ? o.stable_id : undefined,
    label: typeof o.label === 'string' ? o.label : undefined,
    role: typeof o.role === 'string' ? o.role : undefined,
    peer_type: typeof o.peer_type === 'string' ? o.peer_type : undefined,
    host: typeof o.host === 'string' ? o.host : undefined,
    route_name: typeof o.route_name === 'string' ? o.route_name : undefined,
    env: typeof o.env === 'string' ? o.env : undefined,
    public_ui: typeof o.public_ui === 'string' ? o.public_ui : undefined,
    public_ui_published: typeof o.public_ui_published === 'boolean'
      ? o.public_ui_published
      : undefined,
    public_codable: typeof o.public_codable === 'string' ? o.public_codable : undefined,
    public_codable_published: typeof o.public_codable_published === 'boolean'
      ? o.public_codable_published
      : undefined,
    is_local: o.is_local === true,
    enabled: o.enabled !== false,
    last_seen: (o.last_seen as string | null | undefined) ?? null,
    online,
    status: typeof o.status === 'string'
      ? o.status
      : online === true
      ? 'online'
      : online === false
      ? 'offline'
      : undefined,
    announce_age_s: typeof o.announce_age_s === 'number' ? o.announce_age_s : null,
  };
}

/**
 * Sdílený seznam nodů — react-query deduplikuje napříč pickery i Trust Anchors.
 * queryFn VŽDY vrací MeshPeer[] (ne raw {nodes}), ať cache není nekonzistentní.
 */
export function useMeshPeers(opts?: { includeLocal?: boolean }) {
  const includeLocal = opts?.includeLocal !== false;
  return useQuery({
    queryKey: ['mesh-peers-list', includeLocal ? 'with-local' : 'peers-only'],
    queryFn: async () => {
      const [peerResult, topologyResult] = await Promise.allSettled([
        api.mcp<unknown>('mesh.peers_list', { include_local: includeLocal }, { hostLocal: true }),
        api.mcp<unknown>('mesh.topology', {}, { hostLocal: true }),
      ]);
      if (peerResult.status === 'rejected' && topologyResult.status === 'rejected') {
        throw peerResult.reason;
      }
      const peers = peerResult.status === 'fulfilled' ? normalizeMeshPeers(peerResult.value) : [];
      return mergeReachableNodeTargets(
        peers,
        topologyResult.status === 'fulfilled' ? topologyResult.value : null,
      ).filter((peer) => includeLocal || !peer.is_local);
    },
    staleTime: 15_000,
  });
}

/** Presentation only: wire UUID in, familiar node name out. */
export function useNodeDisplayLabel(
  value: string | null | undefined,
  fallback?: string,
): string {
  const { data: peers } = useMeshPeers({ includeLocal: true });
  return resolveNodeDisplayLabel(value, peers ?? [], fallback);
}

export function NodePicker(
  { value, onChange, size = 'sm' }: {
    value: string | null;
    onChange: (node: string | null) => void;
    size?: 'sm' | 'md';
  },
) {
  const { t } = useTranslation();
  const { data: peers } = useMeshPeers();
  // Jeden termín „Lokál" (i18n) — bez připojování názvu nodu, ať je konzistentní
  // s node chipem na kartě.
  const list = Array.isArray(peers) ? peers : [];
  // Picker je přepínač cíle widgetu — NE status board. Offline badge tady
  // mástí (announce age ≠ „node neodpovídá“; mTLS často pořád jede).
  // Membership: všichni enabled peery; liveness patří do Mesh/Infrastruktury.
  const local = list.find((p) => p.is_local);
  const options = [
    {
      value: '',
      label: local
        ? nodeDisplayLabel(local)
        : t('common.localNode', 'Lokál'),
    },
    ...list
      .filter((p) => !p.is_local && p.enabled !== false)
      .map((p) => {
        const wire = p.stable_id || p.name;
        return { value: wire, label: nodeDisplayLabel(p) };
      }),
  ];
  // Normalize legacy URL values (label/route) → wire id so Select matches option
  const wireValue = (() => {
    if (!value) return '';
    const hit = list.find((p) =>
      p.stable_id === value || p.name === value || p.label === value ||
      p.route_name === value || p.host === value
    );
    return hit ? (hit.stable_id || hit.name) : value;
  })();
  return (
    <Select
      size={size}
      options={options}
      value={wireValue}
      onChange={(e) => onChange(e.target.value || null)}
    />
  );
}
